import React from "react";
import { motion } from "framer-motion";

const IndividualHelpCenterInfo = () => {
    return (
        <div className="min-h-screen bg-white py-12 px-6 md:px-10 text-[15px] leading-relaxed">
            <motion.div className="mx-auto max-w-8xl mb-12 border-b border-violet-200 pb-4 max-md:pb-0 flex justify-between items-center"
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            >
                <div>
                    <h1 className="text-3xl font-bold mb-2 max-md:mt-5">User Documentation</h1>
                    <p className="text-sm text-violet-600">
                        Last updated: {new Date().toLocaleDateString()}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-gray-600">Need assistance?</span>
                    <a
                        href="https://selfsync.ai/contact-us"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-gradient text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
                    >
                        Contact Us
                    </a>
                </div>
            </motion.div>

            <div className="flex flex-col lg:flex-row gap-10 max-w-8xl mx-auto">
                {/* Main Content */}
                <motion.div className="flex-1 space-y-12"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <section id="introduction">
                        <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
                        <p className="text-gray-700 mb-3">
                            This document provides a comprehensive and detailed explanation of the various features, tools, and resources available within the SelfSync.ai platform. It is designed to assist users in understanding the platform's capabilities and to help them navigate and utilize each section effectively to meet their professional or organizational goals.
                        </p>
                        <p className="text-gray-700">
                            If you require additional support, guidance, or clarification regarding any aspect of the platform, we encourage you to reach out to our support team by emailing us directly at <a href="mailto:support@selfsync.ai" className="text-violet-600 underline">support@selfsync.ai</a>. Our dedicated team is always here to assist you.
                        </p>
                    </section>

                    <section id="dashboard">
                        <h2 className="text-xl font-semibold mb-4">2. Dashboard Overview</h2>
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li><strong>Total Assessment Count:</strong> Shows the number of assessments you've created on the platform.</li>
                            <li><strong>Completed & Incomplete Assessments:</strong> Tracks which assessments have been completed and which are pending.</li>
                            <li><strong>Credit Info:</strong> Each assessment deducts 1 credit.</li>
                            <li><strong>Transaction History:</strong> View all payment activities and credit usage logs.</li>
                        </ul>
                    </section>

                    <section id="assessments">
                        <h2 className="text-xl font-semibold mb-4">3. Assessment Types</h2>
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li><strong>Fresheres:</strong> Perfect for freshers exploring their strengths. Take the assessment to discover your personality and career fit.</li>
                            <li><strong>Existing Employee:</strong> Ideal for self-evaluation and career growth. Understand your personality traits to align better with your goals.</li>
                            <li><strong>Assessment Duration:</strong> 60 questions, estimated time 45 minutes.</li>
                        </ul>
                    </section>

                    <section id="results">
                        <h2 className="text-xl font-semibold mb-4">4. Assessment Results</h2>
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li><strong>Report Overview:</strong> View results from your assessments.</li>
                            <li><strong>Status Info:</strong> Check whether the report is generated or pending.</li>
                            <li><strong>View Button:</strong> Opens a detailed report with insights and recommendations.</li>
                            <li><strong>Share Option:</strong> Share instantly via WhatsApp, email, or by copying a link.</li>
                        </ul>
                    </section>

                    <section id="profile">
                        <h2 className="text-xl font-semibold mb-4">5. My Profile</h2>
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li><strong>Update Details:</strong> Keep your personal info updated.</li>
                            <li><strong>Subscriptions:</strong> View and manage your current plan.</li>
                            <li><strong>Notifications:</strong> Add emails to receive platform alerts.</li>
                        </ul>
                    </section>

                    <section id="credits">
                        <h2 className="text-xl font-semibold mb-4">6. Credits </h2>
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li><strong>Credit Usage:</strong> Each assessment deducts 1 credit.</li>
                        </ul>
                    </section>

                    <section id="support">
                        <h2 className="text-xl font-semibold mb-4">7. Help & Support</h2>
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li><strong>Help Center:</strong> Browse articles and FAQs anytime.</li>
                            <li><strong>Talk to Our Team:</strong> Send us a message—we reply within 12 hours.</li>
                            <li><strong>Community Forum:</strong> Collaborate and share with other users.</li>
                        </ul>
                    </section>
                </motion.div>

                {/* Sidebar Navigation */}
                <motion.div className="w-full lg:w-64 sticky top-24 h-fit bg-white border-l border-gray-200 pl-4"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <h3 className="text-lg font-semibold mb-4">Quick Navigation</h3>
                    <ul className="text-sm text-violet-700 space-y-2">
                        <li><a href="#introduction" className="hover:underline">1. Introduction</a></li>
                        <li><a href="#dashboard" className="hover:underline">2. Dashboard Overview</a></li>
                        <li><a href="#assessments" className="hover:underline">3. Assessment Types</a></li>
                        <li><a href="#results" className="hover:underline">4. Assessment Results</a></li>
                        <li><a href="#profile" className="hover:underline">5. My Profile</a></li>
                        <li><a href="#credits" className="hover:underline">6. Credits & Referral</a></li>
                        <li><a href="#support" className="hover:underline">7. Help & Support</a></li>
                    </ul>
                </motion.div>
            </div>
        </div>
    )
}

export default IndividualHelpCenterInfo